import React, { Component } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import URLSearchParams from 'url-search-params';

import { Loading, COUNTRIES, SERVER_ERROR } from 'commons';
import { PasswordStrength, passwordErrors } from 'commons';
import request from '../request';

import styles from './Signup.module.css';

const INVALID_REQUEST = { loading: false, error: 'Sorry! Invalid request. Please start over again.' };

const INVALID_FORM = { loading: false, failure: true, error: 'We could not process your request.' };

const recaptchaRef = React.createRef();

// Since 'google.com' is probably blocked for China users, we set `useRecaptchaNet` to true so that the component uses 'recaptcha.net' instead
window.recaptchaOptions = { useRecaptchaNet: true };

class Signup extends Component {
  state = {
    action: 'request',
    loading: true,
    username: '',
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    country: '',
    region: '',
    marketingCompany: '',
    jobTitle: '',
    marketingProfiling: false,
    newsletterOptIn: false,
    tosApproval: false,
    error: false,
    success: false,
    failure: false,
    passwordSyntaxErrors: [],
    reCaptchaSiteKey: '',
    reCaptchaToken: '',
    requestId: '',
    emailReadOnly: false,
    handledAbroad: false
  };

  componentDidMount() {
    this.load();
  }

  load = async () => {
    try {
      const params = new URLSearchParams(window.location.search);
      const formData = [`action=${this.state.action}`];

      if (params.has('sign_up_token')) {
        formData.push(`sign_up_token=${params.get('sign_up_token')}`);
      }

      if (params.has('client_id')) {
        formData.push(`client_id=${params.get('client_id')}`);
      }

      if (params.has('state')) {
        formData.push(`state=${params.get('state')}`);
      }

      if (params.has('requestId')) {
        formData.push(`requestId=${params.get('requestId')}`);
      }

      const res = await request('/session/signup', formData.join('&'));

      if (res.status > 200) {
        this.setState(INVALID_REQUEST);
        return;
      }

      const data = await res.json();
      if (data.action !== 'signup') return;

      this.setState({ action: data.action, requestId: data.requestId, reCaptchaSiteKey: data.reCaptchaSiteKey });
      this.setState({ loading: false });

      // set email and mark read-only if provided using an invite
      if (data.email && data.email.length > 0) {
        this.setState({ email: data.email, emailReadOnly: true, handledAbroad: data.handledAbroad })
      }
    } catch (error) {
      this.setState({ loading: false, ...SERVER_ERROR });
    }
  }

  handleChange = e => this.setState({ [e.target.name]: e.target.value });
  handleCountryChange = e => this.setState({ country: e.target.value, region: '' });
  handleRegionChange = e => this.setState({ region: e.target.value });
  handlePasswordChange = password => this.setState({ password });
  handleCheckboxChange = e => this.setState({ [e.target.name]: e.target.checked });

  handleSubmit = async e => {
    e.preventDefault();

    if (this.state.passwordSyntaxErrors.length > 0) {
      this.setState({ failure: true });
      return;
    }

    this.setState({ loading: true });

    const params = new URLSearchParams(window.location.search);
    const formData = [
      `username=${encodeURIComponent(this.state.username)}`,
      `firstName=${encodeURIComponent(this.state.firstName)}`,
      `lastName=${encodeURIComponent(this.state.lastName)}`,
      `email=${encodeURIComponent(this.state.email)}`,
      `password=${encodeURIComponent(this.state.password)}`,
      `country=${encodeURIComponent(this.state.country)}`,
      `region=${encodeURIComponent(this.state.region)}`,
      `marketingCompany=${encodeURIComponent(this.state.marketingCompany)}`,
      `jobTitle=${encodeURIComponent(this.state.jobTitle)}`,
      `tosApproval=${encodeURIComponent(this.state.tosApproval)}`,
      `marketingProfiling=${encodeURIComponent(this.state.marketingProfiling)}`,
      `newsletterOptIn=${encodeURIComponent(this.state.newsletterOptIn)}`,
      `requestId=${encodeURIComponent(this.state.requestId)}`,
      `g-recaptcha-response=${encodeURIComponent(this.state.reCaptchaToken)}`,
      `action=${this.state.action}`
    ];

    if (params.has('sign_up_token')) {
      formData.push(`sign_up_token=${params.get('sign_up_token')}`);
    }

    if (params.has('client_id')) {
      formData.push(`client_id=${params.get('client_id')}`);
    }

    if (params.has('state')) {
      formData.push(`state=${params.get('state')}`);
    }

    if (params.has('scope')) {
      formData.push(`scope=${params.get('scope')}`);
    }

    try {
      const res = await request('/session/signup', formData.join('&'));

      if (res.status >= 500) {
        this.setState({ loading: false, ...SERVER_ERROR });
        return;
      } else if (res.status >= 400) {
        this.setState(INVALID_FORM);
        return;
      }

      const data = await res.json();

      if (data.action === 'confirmed') {
        window.location.href = "/session/signup/" + data.token;
      } else {
        this.setState({ loading: false, success: true });
      }

    } catch (error) {
      this.setState({ loading: false, ...SERVER_ERROR });
    }
  };

  tryAgain = e => {
    this.setState({ failure: false, error: false, success: false, loading: true, action: 'request' }, this.load);
  };

  verifyEmail = async e => {
    e.preventDefault();
    const email = e.target.value;
    if (email.includes("@")) {
      const res = await request('/session/signup/verify/email', `email=${encodeURIComponent(email)}`);
      const data = await res.json();
      this.setState({ handledAbroad: data.handledAbroad })
    } else {
      this.setState({ handledAbroad: false })
    }
  }

  verifyPasswordSyntax = async password => {
    this.setState({ passwordSyntaxErrors: [] });
    if (password.length <= 0) return;

    try {
      const res = await request('/validate-password', `password=${encodeURIComponent(password)}`);

      if (res.status === 500) {
        this.setState({ passwordSyntaxErrors: ['Unexpected Error'] });
        return;
      }

      const data = await res.json();
      if (data.errors.length > 0) {
        this.setState({ passwordSyntaxErrors: passwordErrors(data.errors) });
      }
    } catch (error) {
      this.setState({ passwordSyntaxErrors: ['Unexpected Error'] });
    }
  };

  renderForm() {
    if (this.state.loading) return <Loading />;
    if (this.state.success)
      return (
        <>
          <p className="invite">
            An activation email has been sent to <span style={{ fontWeight: 400 }}>{this.state.email}</span>
          </p>
          <p className="font-smaller">
            Please check your junk/spam folder if you do not see a Sign Up Request email in your inbox.
          </p>
        </>
      );
    if (this.state.error)
      return (
        <>
          <div className="margin-top-large">{this.state.error}</div>
          {this.state.failure && <div className="margin-top-medium"><button className="btnLink" onClick={this.tryAgain}>Please try again</button></div>}
        </>
      );

    let filteredRegions = [];
    if (this.state.country) {
      const selectedCountry = COUNTRIES.find(country => country.value === this.state.country);
      filteredRegions = (selectedCountry && selectedCountry.regions) || [];
    }

    return (
      <div>
        <p className="invite">Create a new User Account</p>
        {this.state.failure && <div className="message-error">Please verify your password</div>}
        <form className="form-fixed-width-medium margin-top-xlarge" onSubmit={this.handleSubmit}>
          <div className="margin-bottom-xlarge text-align-left">
            <label className="mandatory">
              Email *
              <input name="email" readOnly={this.state.emailReadOnly} value={this.state.email} onChange={this.handleChange} onBlur={this.verifyEmail} type="email" required autoFocus />
            </label>
          </div>
          {this.state.handledAbroad ?
            (
              <div className="margin-bottom-xlarge text-align-left">
                <label className="mandatory">
                  Password
                  <div className={styles.credentialsAbroad}>Credentials handled by an external Identity Provider configured for your company.</div>
                </label>
              </div>
            ) :
            (
              <div className="margin-bottom-xlarge text-align-left">
                <label className="mandatory">
                  Password *
                <PasswordStrength
                    value={this.state.password}
                    onChange={this.handlePasswordChange}
                    errors={this.state.passwordSyntaxErrors}
                    onBlur={this.verifyPasswordSyntax}
                    displayBar={this.state.password.length > 0}
                  />
                </label>
              </div>
            )
          }
          <div className="margin-bottom-xlarge text-align-left">
            <label className="mandatory">
              First Name *
              <input name="firstName" value={this.state.firstName} onChange={this.handleChange} type="text" required />
            </label>
          </div>
          <div className="margin-bottom-xlarge text-align-left">
            <label className="mandatory">
              Last Name *
              <input name="lastName" value={this.state.lastName} onChange={this.handleChange} type="text" required />
            </label>
          </div>
          <div className="margin-bottom-xlarge text-align-left">
            <label className="mandatory">
              Company *
              <input name="marketingCompany" value={this.state.marketingCompany} onChange={this.handleChange} type="text" required />
            </label>
          </div>
          <div className="margin-bottom-xlarge text-align-left">
            <label className="mandatory">
              Job Title *
              <input name="jobTitle" value={this.state.jobTitle} onChange={this.handleChange} type="text" required />
            </label>
          </div>
          <div className="margin-bottom-xlarge text-align-left">
            <label className="mandatory">
              Country *
              <select name="country" value={this.state.country} onChange={this.handleCountryChange} required>
                <option disabled hidden value="" />
                {COUNTRIES.map(country => (
                  <option key={country.value} value={country.value}>
                    {country.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {filteredRegions && filteredRegions.length > 0 && (
            <div className="margin-bottom-xlarge text-align-left">
              <label className="mandatory">
                State/Province *
                <select name="region" value={this.state.region} onChange={this.handleRegionChange} required>
                  <option disabled hidden value="" />
                  {filteredRegions.map(region => (
                    <option key={region} value={region}>
                      {region}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}
          <div className="margin-bottom-xlarge text-align-left potdemiel">
            <label className="mandatory">
              Do not fill this input
              <input name="username" value={this.state.username} onChange={this.handleChange} type="text" autoComplete="off" tabIndex="-1" />
            </label>
          </div>
          <div className="margin-bottom-xlarge text-align-left">
            <label className={styles.indented}>
              <input
                className="margin-right-medium"
                name="tosApproval"
                type="checkbox"
                checked={this.state.tosApproval}
                onChange={this.handleCheckboxChange}
                required
              />
              * I agree to the{' '}
              <a href="https://www.sierrawireless.com/terms-of-use" target="_blank" rel="noopener noreferrer">
                Terms of Use
              </a>{' '}
              and I am informed that my personal data is processed in accordance with the{' '}
              <a href="https://login.sierrawireless.io/user-notice" target="_blank" rel="noopener noreferrer">
                User Information Notice
              </a>
            </label>
          </div>
          <div className="margin-bottom-xlarge text-align-left">
            <label className={styles.indented}>
              <input
                className="margin-right-medium"
                name="marketingProfiling"
                type="checkbox"
                checked={this.state.marketingProfiling}
                onChange={this.handleCheckboxChange}
              />
              I allow Sierra Wireless to use my personal data for marketing profiling
            </label>
            <div className={styles.fontSmallerIndented}>By checking this box, you consent Sierra Wireless Inc. and Sierra Wireless group companies to use your personal data for marketing profiling advertising for Sierra Wireless products and services. You can check the <a href="https://login.sierrawireless.io/user-notice" alt="Privacy Policy" target="_blank" rel="noopener noreferrer">User Information Notice</a> to know more about how we process your data.</div>
          </div>
          <div className="margin-bottom-xlarge text-align-left">
            <label className={styles.indented}>
              <input
                className="margin-right-medium"
                name="newsletterOptIn"
                type="checkbox"
                checked={this.state.newsletterOptIn}
                onChange={this.handleCheckboxChange}
              />
              I would like to receive future communications from Sierra Wireless
            </label>
            <div className={styles.fontSmallerIndented}>By checking this box, you consent to receive commercial electronic messages from Sierra Wireless Inc. and Sierra Wireless group companies such as newsletters, event invitations, promotional and educational content, product update, transaction-related emails, and customer service emails in accordance with our <a href="https://login.sierrawireless.io/user-notice" alt="Privacy Policy" target="_blank" rel="noopener noreferrer">User Information Notice</a>.</div>
          </div>
          <div style={{ display: "inline-block" }}>
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={this.state.reCaptchaSiteKey}
              onChange={reCaptchaToken => this.setState({ reCaptchaToken })}
            />
          </div>
          <div className="margin-top-large">
            <input className="btn" type="submit" value="SIGN UP" disabled={!this.state.reCaptchaToken} />
          </div>
        </form>
        <div className="margin-top-medium">
          <button className="btnLink" onClick={this.props.history.goBack}>Go Back</button>
        </div>
      </div>
    );
  }

  render() {
    return <div className="boxed-container">{this.renderForm()}</div>;
  }
}

export default Signup;
